import { useEffect, useRef } from 'react';
import { useI18n } from './LanguageContext';

const LOCALIZABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label'] as const;

function canTranslateTextNode(node: Text) {
  const parent = node.parentElement;
  if (!parent) return false;
  if (parent.closest('[data-no-ui-translate="true"]')) return false;
  if (parent.closest('script,style,noscript,code,pre,kbd,samp')) return false;
  if (parent.closest('[contenteditable="true"]')) return false;
  return true;
}

export function UiTextLocalizationBridge() {
  const { language, tr } = useI18n();
  const originalTextRef = useRef(new WeakMap<Text, string>());
  const originalAttrsRef = useRef(new WeakMap<Element, Partial<Record<(typeof LOCALIZABLE_ATTRIBUTES)[number], string>>>());
  const applyingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || language === 'en') return undefined;

    const root = document.getElementById('root');
    if (!root) return undefined;

    const applyTextNode = (textNode: Text) => {
      if (!canTranslateTextNode(textNode)) return;
      const currentValue = textNode.nodeValue ?? '';
      const existingOriginal = originalTextRef.current.get(textNode);
      const original = existingOriginal ?? currentValue;
      const trimmed = original.trim();
      if (!trimmed || trimmed.length > 140) return;
      const translated = tr(trimmed);
      if (translated === trimmed) return;
      if (!existingOriginal) {
        originalTextRef.current.set(textNode, original);
      }
      const nextValue = original.replace(trimmed, translated);
      if (currentValue !== nextValue) {
        textNode.nodeValue = nextValue;
      }
    };

    const applyElementAttributes = (element: Element) => {
      if (element.closest('[data-no-ui-translate="true"]')) return;
      let originalEntry = originalAttrsRef.current.get(element);
      for (const attr of LOCALIZABLE_ATTRIBUTES) {
        const currentValue = element.getAttribute(attr);
        if (!currentValue) continue;
        const original = originalEntry?.[attr] ?? currentValue;
        const translated = tr(original);
        if (translated !== original && currentValue !== translated) {
          if (!originalEntry) {
            originalEntry = {};
            originalAttrsRef.current.set(element, originalEntry);
          }
          if (!originalEntry[attr]) {
            originalEntry[attr] = currentValue;
          }
          element.setAttribute(attr, translated);
        }
      }
    };

    const applySubtree = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        applyTextNode(node as Text);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as Element;
      applyElementAttributes(element);

      const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      let current = walker.currentNode;
      while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
          applyTextNode(current as Text);
        } else if (current.nodeType === Node.ELEMENT_NODE) {
          applyElementAttributes(current as Element);
        }
        current = walker.nextNode();
      }
    };

    const applyAll = () => {
      if (applyingRef.current) return;
      applyingRef.current = true;
      try {
        applySubtree(root);
      } finally {
        applyingRef.current = false;
      }
    };

    applyAll();

    const observer = new MutationObserver((mutations) => {
      if (applyingRef.current) return;
      for (const mutation of mutations) {
        if (mutation.type === 'characterData' && mutation.target.nodeType === Node.TEXT_NODE) {
          applyTextNode(mutation.target as Text);
          continue;
        }
        if (mutation.type === 'attributes' && mutation.target.nodeType === Node.ELEMENT_NODE) {
          applyElementAttributes(mutation.target as Element);
          continue;
        }
        mutation.addedNodes.forEach((added) => applySubtree(added));
      }
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...LOCALIZABLE_ATTRIBUTES],
    });

    return () => observer.disconnect();
  }, [language, tr]);

  useEffect(() => {
    if (typeof window === 'undefined' || language !== 'en') return;
    const root = document.getElementById('root');
    if (!root) return;

    const resetText = (textNode: Text) => {
      const original = originalTextRef.current.get(textNode);
      if (typeof original === 'string' && textNode.nodeValue !== original) {
        textNode.nodeValue = original;
      }
    };

    const resetAttrs = (element: Element) => {
      const originalAttrs = originalAttrsRef.current.get(element);
      if (!originalAttrs) return;
      for (const attr of LOCALIZABLE_ATTRIBUTES) {
        const original = originalAttrs[attr];
        if (!original) continue;
        if (element.getAttribute(attr) !== original) {
          element.setAttribute(attr, original);
        }
      }
    };

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let node = walker.currentNode;
    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        resetText(node as Text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        resetAttrs(node as Element);
      }
      node = walker.nextNode();
    }
  }, [language]);

  return null;
}
